import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
} from "recharts";
import { GetOrdersActivityChartData } from "../../stores/admin";

type CustomTooltipProps = {
  active?: boolean;
  payload?: any[];
  label?: string;
};

const productNameMap: Record<number, string> = {
  1: "ITIN",
  2: "LLC",
  3: "Tax filing",
  4: "Invoices",
  5: "OA",
  6: "Bookkeeper"
};

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload?.length) return null;

  const item = payload[0].payload;
  return (
    <div
      style={{
        background: "#fff",
        padding: "14px",
        borderRadius: "5px",
        boxShadow: "0 20px 20px rgba(0, 0, 0, 0.1)",
      }}
    >
      <p
        style={{
          fontWeight: 600,
          fontSize: "20px",
          color: "#2977CC",
        }}
      >
        {productNameMap[item.product_id] || label}
      </p>
      <p
        style={{
          marginTop: 10,
          color: "#1b1a1aff",
          fontSize: "14px",
          fontWeight: 500,
        }}
      >
        Count: <span>{item.count}</span>
      </p>
      <p
        style={{
          color: "#1b1a1aff",
          fontSize: "14px",
          fontWeight: 500,
        }}
      >
        Amount: <span>${item.amount}</span>
      </p>
    </div>
  );
};

const AdminChart = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);

  useEffect(() => {
    getChartData();
  }, []);

  const getChartData = async () => {
    const response = await dispatch<any>(GetOrdersActivityChartData());
    const apiData = response?.payload?.data || [];

    // ✅ Map product_id to name for X-axis
    const formattedData = apiData.map((item: any) => ({
      ...item,
      name: productNameMap[item.product_id] || `Product ${item.product_id}`,
    }));

    setData(formattedData);
  };

  return (
    <div
      style={{
        background: "#fff",
        border: "2px solid #EAF1FA",
        borderRadius: "8px",
        padding: "20px",
        marginTop: 40,
      }}
    >
      {/* Title */}
      <h2
        style={{
          color: "#2977CC",
          fontSize: "20px",
          fontWeight: "600",
          marginBottom: "20px",
        }}
      >
        Orders Activity
      </h2>

      {data && (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={data}
            barSize={50}
            margin={{
              top: 10,
              right: 30,
              left: 30,
              bottom: 40,
            }}
          >
            <CartesianGrid strokeDasharray="1 1" vertical={false} />

            {/* Y-Axis with label */}
            <YAxis>
              <Label
                value="Orders Count"
                offset={20}
                angle={-90}
                position="left"
                style={{
                  textAnchor: "middle",
                  fontSize: "16px",
                  fill: "#1d62acff",
                  fontWeight: "500",
                }}
              />
            </YAxis>

            {/* X-Axis with label */}
            <XAxis dataKey="name">
              <Label
                value="Services"
                offset={20}
                position="bottom"
                style={{
                  textAnchor: "middle",
                  fontSize: "16px",
                  fill: "#1d62acff",
                  fontWeight: "500",
                }}
              />
            </XAxis>

            <Tooltip content={<CustomTooltip />} cursor={false} />

            <Bar
              dataKey="count"
              fill="#2977CC"
              activeBar={<Rectangle fill="#0e3c6eff" />}
              style={{
                cursor: "pointer",
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default AdminChart;
